'use client'

import { ButtonSpinner, type ButtonSpinnerTone } from '@tebuto/layout/Spinner'
import { cn } from '@tebuto/lib/utils'
import { LucideIcon } from 'lucide-react'

// ── Design-system vocabulary (see Tebuto refactor spec) ──────────────────────
// One prop per visual concept, one value scale, shared across the DS.
export type ButtonVariant = 'solid' | 'outline' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'
export type ButtonColor = 'primary' | 'neutral' | 'success' | 'warning' | 'danger'
export type ButtonType = 'button' | 'submit' | 'reset'
export type IconPosition = 'before' | 'after'

// ── Deprecated aliases (mapped through for one minor version) ────────────────
/** @deprecated use `variant: 'ghost'` */
export type ButtonLayout = 'default' | 'ghost'
/** @deprecated use the semantic `color` scale */
export type LegacyButtonColor = 'primary' | 'red' | 'blue' | 'orange' | 'secondary' | 'white' | 'gray' | 'lightgray'

// HTMLProps (not ComponentProps) preserves the loose attr surface callers rely
// on (download/target on link-buttons, etc.); we redefine color/size/type.
type NativeButtonProps = Omit<React.HTMLProps<HTMLButtonElement>, 'color' | 'size' | 'type' | 'ref'>

type ButtonProps = {
    href?: string
    variant?: ButtonVariant
    size?: ButtonSize
    /** Semantic scale; legacy values are still accepted and mapped (deprecated). */
    color?: ButtonColor | LegacyButtonColor
    isLoading?: boolean
    icon?: LucideIcon
    iconPosition?: IconPosition
    className?: string
    disabled?: boolean
    type?: ButtonType
    // ── deprecated aliases ──
    /** @deprecated use `variant: 'ghost'` (or `'outline'`) */
    layout?: ButtonLayout
    /** @deprecated use `variant: 'outline'` */
    outline?: boolean
} & NativeButtonProps

const warned = new Set<string>()
function warnOnce(key: string, message: string) {
    if (process.env.NODE_ENV === 'production' || warned.has(key)) return
    warned.add(key)
    console.warn(`[tebuto/Button] ${message}`)
}

const COLOR_ALIAS: Record<LegacyButtonColor, ButtonColor> = {
    primary: 'primary',
    red: 'danger',
    blue: 'neutral',
    orange: 'warning',
    secondary: 'neutral',
    white: 'neutral',
    gray: 'neutral',
    lightgray: 'neutral'
}

function isNewColor(c: string): c is ButtonColor {
    return c === 'primary' || c === 'neutral' || c === 'success' || c === 'warning' || c === 'danger'
}

/** Resolve effective variant/size/color from new props + deprecated aliases. */
function resolveProps(p: ButtonProps): { variant: ButtonVariant; size: ButtonSize; color: ButtonColor } {
    let variant: ButtonVariant = p.variant ?? 'solid'
    if (p.variant == null) {
        if (p.layout === 'ghost') {
            variant = 'ghost'
            warnOnce('layout', '`layout="ghost"` is deprecated — use `variant="ghost"`.')
        } else if (p.outline) {
            variant = 'outline'
            warnOnce('outline', '`outline` is deprecated — use `variant="outline"`.')
        }
    }

    const size: ButtonSize = p.size ?? 'md'

    let color: ButtonColor = 'primary'
    const c = p.color as ButtonColor | LegacyButtonColor | undefined
    if (c != null) {
        if (isNewColor(c)) {
            color = c
        } else if (c in COLOR_ALIAS) {
            color = COLOR_ALIAS[c as LegacyButtonColor]
            warnOnce(`color:${c}`, `\`color="${c}"\` is deprecated — use the semantic scale (mapped to "${color}").`)
        }
    }
    return { variant, size, color }
}

// Separate component for buttons with href. Uses window.location to avoid a
// Next.js router dependency in widget context (full reload in app context).
function ButtonWithHref({ children, href, ...rest }: ButtonProps & { href: string }) {
    const handleClick = () => {
        window.location.href = href
    }
    return (
        <ButtonContent onClick={handleClick} {...rest}>
            {children}
        </ButtonContent>
    )
}

export function Button({ children, href, onClick, ...rest }: ButtonProps) {
    if (href) {
        if (onClick) {
            throw new Error('Button cannot have both href and onClick')
        }
        return (
            <ButtonWithHref href={href} {...rest}>
                {children}
            </ButtonWithHref>
        )
    }
    return (
        <ButtonContent onClick={onClick} {...rest}>
            {children}
        </ButtonContent>
    )
}

function ButtonContent(props: ButtonProps) {
    const {
        children,
        className,
        type = 'button',
        disabled = false,
        isLoading = false,
        icon: Icon,
        iconPosition = 'before',
        // strip DS + deprecated props so they don't reach the DOM
        variant: _variant,
        size: _size,
        color: _color,
        layout: _layout,
        outline: _outline,
        ...domProps
    } = props

    const { variant, size, color } = resolveProps(props)

    const focusRingClass = color === 'danger' ? 'focus:ring-danger/45' : 'focus:ring-primary-brand/50'
    const styling = cn(
        'relative w-max rounded font-medium flex justify-center items-center whitespace-nowrap transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 group',
        focusRingClass,
        variant === 'solid' && 'shadow-sm',
        variant === 'ghost' && 'rounded-lg',
        colorClasses(color, variant),
        sizeClasses(size),
        disabled && 'cursor-not-allowed opacity-50',
        className
    )

    const iconSize = getIconSize(size)
    const iconSpacing = getIconSpacing(size)

    return (
        <button
            type={type}
            className={styling}
            onMouseDown={(event) => event.preventDefault()}
            disabled={disabled || isLoading}
            {...domProps}
        >
            <div className={cn(isLoading ? 'absolute inset-0 flex items-center justify-center' : 'hidden')}>
                <ButtonSpinner tone={getSpinnerTone(color, variant)} />
            </div>
            <div className={cn(isLoading ? 'invisible' : '', 'flex items-center justify-center')}>
                {Icon && iconPosition === 'before' && (
                    <Icon size={iconSize} className={cn(children ? iconSpacing.before : '', 'opacity-80')} />
                )}
                {children}
                {Icon && iconPosition === 'after' && (
                    <Icon size={iconSize} className={cn(children ? iconSpacing.after : '', 'opacity-80')} />
                )}
            </div>
        </button>
    )
}

function getIconSize(size: ButtonSize): number {
    switch (size) {
        case 'lg':
            return 20
        case 'sm':
            return 14
        default:
            return 16
    }
}

function getIconSpacing(size: ButtonSize) {
    switch (size) {
        case 'lg':
            return { before: 'mr-3', after: 'ml-3' }
        case 'sm':
            return { before: 'mr-1.5', after: 'ml-1.5' }
        default:
            return { before: 'mr-2', after: 'ml-2' }
    }
}

function sizeClasses(size: ButtonSize) {
    switch (size) {
        case 'lg':
            return 'sm:text-lg text-base sm:px-6 sm:py-3 px-3 py-1.5 sm:h-13.75 h-12'
        case 'sm':
            return 'sm:text-xxs text-xxs px-2 py-1 max-sm:py-2'
        default:
            return 'sm:text-sm text-xs sm:px-4 sm:py-2 px-2 py-1 sm:h-9 h-8'
    }
}

function getSpinnerTone(color: ButtonColor, variant: ButtonVariant): ButtonSpinnerTone {
    if (variant === 'solid') {
        return 'on-inverse'
    }
    if (color === 'danger') {
        return 'destructive'
    }
    if (color === 'primary') {
        return 'brand'
    }
    return 'muted'
}

// Single mapping of the semantic color scale × variant to token utilities.
function colorClasses(color: ButtonColor, variant: ButtonVariant): string {
    const map: Record<ButtonColor, Record<ButtonVariant, string>> = {
        primary: {
            solid: 'text-white bg-primary-brand hover:bg-primary-hover',
            outline: 'text-primary-brand border border-primary-brand hover:bg-primary-50',
            ghost: 'text-primary-brand bg-transparent hover:bg-primary-50'
        },
        neutral: {
            solid: 'text-white bg-neutral hover:bg-neutral/90',
            outline:
                'text-foreground-secondary border border-neutral-border hover:bg-surface-subtle hover:text-foreground',
            ghost: 'text-foreground-secondary bg-transparent hover:bg-surface-subtle'
        },
        success: {
            solid: 'text-white bg-success hover:bg-success/90',
            outline: 'text-success border border-success-border hover:bg-success-subtle',
            ghost: 'text-success bg-transparent hover:bg-success-subtle'
        },
        warning: {
            solid: 'text-white bg-warning hover:bg-warning/90',
            outline: 'text-warning border border-warning-border hover:bg-warning-subtle',
            ghost: 'text-warning bg-transparent hover:bg-warning-subtle'
        },
        danger: {
            solid: 'text-destructive-foreground bg-danger hover:bg-danger/90',
            outline: 'text-danger border border-danger-border hover:bg-danger-subtle',
            ghost: 'text-danger bg-transparent hover:bg-danger-subtle'
        }
    }
    return map[color][variant]
}
